<template>
  <component
    :is="as"
    class="list-item"
    :class="{ clickable, alert, selectable, split, multi: numFields > 1 }"
    :style="{ '--num-fields': numFields }"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <div class="content">
      <div v-if="selectable" class="checkbox-wrapper">
        <icon-button
          :icon="selected ? 'check_box' : 'check_box_outline_blank'"
          :class="{ success: selected }"
          @click.stop.prevent="$emit('toggleSelected')"
        />
      </div>

      <slot name="image" />

      <slot name="info" />
    </div>

    <div class="fields">
      <card-stat-item
        v-for="(field, i) in fields"
        :key="i"
        :card="card"
        :stat="field"
      />

      <stat-item
        v-for="(customField, i) in customFields"
        :key="i + (fields && fields.length ? fields.length : 0)"
        :label="customField.label"
        :value="customField.value"
      />
    </div>
  </component>
</template>

<script>
import CardStatItem from './CardStatItem';
import StatItem from './StatItem';

export default {
  components: {
    CardStatItem,
    StatItem,
  },

  props: {
    card: Object,
    as: {
      type: String,
      default: 'div',
    },
    fields: Array,
    customFields: Array,
    selected: Boolean,
    selectable: Boolean,
    alert: Boolean,
    split: Boolean,
  },

  computed: {
    clickable() {
      return this.as !== 'div';
    },

    numFields() {
      return (this.fields?.length || 0) + (this.customFields?.length || 0);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../scss/variables.scss';

.list-item {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: $gp;
  padding: $gp;
  color: var(--theme-color);
  text-decoration: none !important;
  text-align: left;
  background-color: transparent;
  border: none;

  &.multi {
    grid-template-columns: 4fr 3fr;

    @media($large-down) {
      grid-template-columns: 1fr;
    }
  }

  &.split {
    grid-template-columns: 1fr;
  }

  .panel-open &.multi {
    @media($xlarge-down) {
      grid-template-columns: 1fr;
    }
  }

  &.clickable {
    cursor: pointer;

    &:hover {
      background-color: var(--theme-highlight);
    }

    &.active,
    &.router-link-exact-active,
    &.nuxt-link-exact-active {
      background-color: var(--theme-active);
    }
  }

  &.alert {
    background-color: rgba($color-red-1, 0.25);

    &:hover {
      background-color: rgba($color-red-1, 0.4);
    }
  }

  + .list-item {
    border-top: solid 1px var(--theme-border);
  }
}

.content {
  --image-width: 58px;
  display: grid;
  grid-template-columns: var(--image-width) 1fr;
  align-items: center;
  gap: $gp;

  .selectable & {
    grid-template-columns: auto var(--image-width) 1fr;
  }

  @media($large-down) {
    --image-width: 54px;
  }
}

@mixin split() {
  display: flex;
  gap: $gp;

  > *:last-child {
    margin-left: auto;
  }

  > *:not(:last-child) {
    --stat-item-justify: flex-start;
    text-align: left;
  }
}

.fields {
  overflow: hidden;
  display: grid;
  grid-template-columns: repeat(var(--num-fields), 1fr);
  align-items: center;
  gap: $gp * 2;
  text-align: right;
  --stat-item-justify: flex-end;

  .multi & {
    @media($large-down) {
      @include split;
    }
  }

  .split & {
    @include split;
  }

  .panel-open .multi & {
    @media($xlarge-down) {
      @include split;
    }
  }
}
</style>
